import { propostaEstaAberta } from '@modules/aawz-intelligence-v2/utils/proposta-aberta'
import { RELATORIO_DO_LOTE, PROPOSTAS_LOTE_TEXT } from '@modules/aawz-intelligence-v2/constants/Text.propostas-lote'
import {
  formatarIdRegistroCurto,
  formatarReciboHora,
  montarTextoCommitada,
  resolverReciboEntidadeLabel,
} from '@modules/aawz-intelligence-v2/components/ia-confirm-action/ia-confirm-action.util'

/**
 * [spec-003 T302 / US-8] Os QUATRO desfechos que o gestor lê, e a tradução do
 * vocabulário do servidor para eles.
 *
 * O `resultado` do evento `decisao` tem CINCO valores
 * (`spec-002/contracts/events.schema.json`, `$defs.d_decisao`):
 * `commitada | rejeitada | recusada | replay | expirada`. Mais os estados que
 * o FRONT grava sozinho no mapa `propostasAbertas`: `erro` (o POST que não
 * chegou), `invalidada` (substituída no fluxo de `editar`) e `expirada` pelo
 * relógio local (`onExpirouPorRelogio` — o servidor nunca empurra evento de
 * expiração, só rejeita a decisão tardia com 409).
 *
 * ★ Duas colagens que este mapa existe para impedir:
 *
 *  - `recusada` ≠ `rejeitada`. Na primeira quem disse não foi o SERVIDOR (hash
 *    mudou, papel mudou, entidade sem writer); na segunda foi o consultor.
 *    Colapsá-las atribuiria ao gestor uma decisão que ele não tomou — e ele
 *    responde por ela.
 *  - `replay` É execução. O backend devolve o MESMO recibo sem gravar de novo
 *    (US-7); classificá-lo como outra coisa diria que a ação não aconteceu.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
export const DESFECHO = Object.freeze({
  EXECUTADA: 'executada',
  NEGADA: 'negada',
  FALHOU: 'falhou',
  NAO_DECIDIDA: 'nao_decidida',
})

/** `resultado` do servidor → desfecho. Valor fora do mapa cai no `null`. */
const DESFECHO_POR_RESULTADO = Object.freeze({
  commitada: DESFECHO.EXECUTADA,
  replay: DESFECHO.EXECUTADA,
  rejeitada: DESFECHO.NEGADA,
  recusada: DESFECHO.FALHOU,
  expirada: DESFECHO.NAO_DECIDIDA,
})

/** Estados que o FRONT grava e que já são desfecho por si. */
const DESFECHO_POR_ESTADO = Object.freeze({
  expirada: DESFECHO.NAO_DECIDIDA,
  invalidada: DESFECHO.NAO_DECIDIDA,
})

/**
 * [T313 / US-8 AC3] Uma linha de `value.propostas` (o RELATÓRIO que
 * `GET /conversas/{id}/run-atual` passou a devolver) → a entrada do mapa
 * `propostasAbertas`.
 *
 * ★ Os DOIS eixos vão juntos, e não só o `resultado`, por duas razões que o
 * backend mediu ao escrever a rota:
 *
 *  - **`invalidada` não tem `resultado`.** O `CHECK ck_iv2_decisoes_resultado`
 *    (v661) só aceita `commitada|rejeitada|recusada`; a proposta substituída
 *    no fluxo de `editar` é classificada pelo `estado`. Sem ele, esse caso
 *    ficaria sem desfecho na tela.
 *  - **`recusada` ≠ `rejeitada`.** A linha de uma recusa traz
 *    `decisao='aprovar'` **e** `resultado='recusada'`: o gestor mandou
 *    executar, quem barrou foi o servidor. Repassar só o `resultado`
 *    atribuiria a ele uma decisão que não tomou — e ele responde por ela.
 *
 * `reidratada` marca a ORIGEM: esta linha veio da trilha persistida em
 * `run_decisoes`, não de um gesto desta sessão. É o que permite ao relatório
 * aparecer depois do F5 sem transformar toda decisão individual em "lote".
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 *
 * @param {Object} linha Um item de `value.propostas` do envelope.
 * @returns {Object} O payload de `upsertProposta`.
 */
export function propostaDoRelatorio(linha) {
  const item = linha || {}
  return {
    id_proposta: item.id_proposta,
    hash_payload: item.hash_payload || null,
    expira_em: item.expira_em || null,
    // CRU, nunca forçado a `'aberta'`: é o eixo que classifica a `invalidada`.
    estado: item.estado || null,
    preview: {
      tool: item.tool || null,
      entidade: item.entidade || null,
      operacao: item.operacao || null,
      alvo: item.alvo || null,
      resumo: item.resumo || null,
    },
    decisao: item.decisao || null,
    resultado: item.resultado || null,
    recibo: item.recibo || null,
    motivo: item.motivo || null,
    reidratada: true,
  }
}

/**
 * O desfecho de UMA proposta, ou `null` quando ela ainda pode ser decidida.
 *
 * ★ `null` não é omissão: a proposta genuinamente aberta não é "o que foi nem
 * o que não foi feito" — é o que continua na mesa, com o cartão dela ainda
 * oferecendo "Aprovar". Classificá-la como "não decidida" diria ao gestor que
 * o prazo acabou.
 *
 * Quem decide "ainda está aberta" é `propostaEstaAberta` — a MESMA função do
 * contador do chip e do card, nunca uma segunda leitura do relógio.
 *
 * @param {Object} proposta A entrada do mapa `propostasAbertas`.
 * @returns {String|null} Um valor de `DESFECHO`, ou `null`.
 */
export function desfechoDaProposta(proposta) {
  if (!proposta) return null

  // O servidor mandou veredito: ele manda. `decidirProposta` limpa o `erro`
  // local no sucesso, então os dois não coexistem na prática — mas a ordem
  // deixa isso explícito em vez de depender daquela limpeza.
  const porResultado = DESFECHO_POR_RESULTADO[proposta.resultado]
  if (porResultado) return porResultado

  const porEstado = DESFECHO_POR_ESTADO[proposta.estado]
  if (porEstado) return porEstado

  // O POST que não chegou ao servidor — é o mesmo campo que acende o "Tentar
  // novamente" do cartão.
  if (proposta.erro) return DESFECHO.FALHOU

  if (propostaEstaAberta(proposta)) return null

  // Sobrou: prazo vencido no relógio local, ou `estado` terminal sem
  // `resultado` (o `end` do run fechou sem que a decisão chegasse).
  return DESFECHO.NAO_DECIDIDA
}

/**
 * O recibo da proposta executada, já em uma linha — ou vazio.
 *
 * Reusa os formatadores do cartão de decisão (`ia-confirm-action.util.js`):
 * duas grafias do mesmo recibo na mesma conversa foi o defeito que a
 * consolidação daquele util fechou.
 */
function reciboEmUmaLinha(proposta) {
  const recibo = (proposta && proposta.recibo) || null
  if (!recibo) return ''
  const hora = formatarReciboHora(recibo.commitado_em)
  if (!hora) return ''
  return montarTextoCommitada({
    reciboHoraFormatada: hora,
    reciboEntidadeLabel: resolverReciboEntidadeLabel(recibo.entidade),
    reciboIdRegistroCurto: formatarIdRegistroCurto(recibo.id_registro),
  })
}

/**
 * O motivo de uma ação SEM recibo — a outra metade da AC ("o motivo quando não
 * houver").
 *
 * O texto do servidor/POST vence a frase da casa quando existe: ele nomeia o
 * que de fato aconteceu, e a nossa é genérica por construção.
 */
function motivoDaLinha(proposta, desfecho) {
  if (desfecho === DESFECHO.EXECUTADA) return ''
  if (desfecho === DESFECHO.FALHOU) {
    return proposta.erro || proposta.motivo || RELATORIO_DO_LOTE.MOTIVO.falhou
  }
  if (desfecho === DESFECHO.NEGADA) {
    return proposta.motivo || RELATORIO_DO_LOTE.MOTIVO.negada
  }
  if (proposta.estado === 'invalidada') return RELATORIO_DO_LOTE.MOTIVO.invalidada
  return proposta.motivo || RELATORIO_DO_LOTE.MOTIVO.expirada
}

/**
 * O mapa `propostasAbertas` → as linhas do relatório, decoradas e prontas para
 * o template (sem função no `<template>`, regra do repo).
 *
 * Ordem: a do mapa, que é a de chegada das propostas — a mesma que o lote
 * percorreu ao decidir.
 *
 * @param {Object} propostas O mapa por `id_proposta`.
 * @returns {Array<Object>} `[{id_proposta, desfecho, rotulo, resumo, recibo, motivo}]`
 */
export function linhasDoRelatorioDoLote(propostas) {
  const mapa = propostas || {}
  return Object.keys(mapa)
    .map(function (id) {
      const proposta = mapa[id] || {}
      const desfecho = desfechoDaProposta(proposta)
      if (!desfecho) return null
      const preview = proposta.preview || {}
      return {
        id_proposta: proposta.id_proposta || id,
        desfecho: desfecho,
        rotulo: RELATORIO_DO_LOTE.ROTULO[desfecho] || '',
        resumo: preview.resumo || PROPOSTAS_LOTE_TEXT.itemSemResumo(proposta.id_proposta || id),
        recibo: desfecho === DESFECHO.EXECUTADA ? reciboEmUmaLinha(proposta) : '',
        motivo: motivoDaLinha(proposta, desfecho),
      }
    })
    .filter(Boolean)
}

/**
 * Quantas ações em cada desfecho — o resumo do cabeçalho.
 *
 * @param {Array<Object>} linhas A saída de `linhasDoRelatorioDoLote`.
 * @returns {Object} `{executada, negada, falhou, nao_decidida}`, sempre com as 4.
 */
export function contagensDoRelatorio(linhas) {
  const contagens = {
    [DESFECHO.EXECUTADA]: 0,
    [DESFECHO.NEGADA]: 0,
    [DESFECHO.FALHOU]: 0,
    [DESFECHO.NAO_DECIDIDA]: 0,
  }
  ;(linhas || []).forEach(function (linha) {
    if (linha && contagens[linha.desfecho] !== undefined) contagens[linha.desfecho] += 1
  })
  return contagens
}
